import React from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";
import LocalText from "../common/LocalText";

const ContentArea = ({ children, title, subtitle, icon, titleAction, container = false }) => {
  const navigate = useNavigate();
  const canGoBack = (window.history.state?.idx ?? 0) > 0;

  // Without a subtitle the title keeps being a direct flex child, exactly as before.
  const titleNode = title && (
    <Typography
      variant="h5"
      fontWeight={800}
      sx={{ color: "#1e3a5f", flexGrow: subtitle ? 0 : 1, minWidth: 0, letterSpacing: "-0.3px", fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.5rem" } }}
    >
      <LocalText>{title}</LocalText>
    </Typography>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, minHeight: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
      {(title || icon) && (
        <Box display="flex" alignItems="center" sx={{ gap: 1.5, mb: 2, flexShrink: 0, flexWrap: 'wrap' }}>

          {/* Back button — white card, dark arrow, slides left on hover */}
          {canGoBack && (
            <Tooltip title="Back" placement="right">
              <IconButton
                onClick={() => navigate(-1)}
                size="small"
                sx={{
                  bgcolor: "white",
                  color: "#4a5568",
                  width: { xs: 40, sm: 40 },
                  height: { xs: 40, sm: 40 },
                  borderRadius: { xs: "10px", sm: "12px" },
                  boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                  flexShrink: 0,
                  "& svg": { transition: "transform 0.2s ease" },
                  "&:hover": {
                    bgcolor: "white",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                    "& svg": { transform: "translateX(-3px)" },
                  },
                  transition: "box-shadow 0.2s ease",
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: { xs: 13, sm: 15 } }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Page icon — dark navy rounded container; accepts a PNG src string or a React element */}
          {icon && (
            <Box
              sx={{
                bgcolor: "#0a326d",
                borderRadius: { xs: "10px", sm: "12px" },
                width: { xs: 36, sm: 44, md: 48 },
                height: { xs: 36, sm: 44, md: 48 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                flexShrink: 0,
                color: "white",
              }}
            >
              {typeof icon === "string" ? (
                <Box
                  component="img"
                  src={icon}
                  alt=""
                  sx={{ width: { xs: 20, sm: 24, md: 28 }, height: { xs: 20, sm: 24, md: 28 }, objectFit: "contain" }}
                />
              ) : icon}
            </Box>
          )}

          {/* Page title — dark navy, readable on light background */}
          {subtitle ? (
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {titleNode}
              <Typography sx={{ color: "#64748b", fontSize: { xs: 11, sm: 12 }, lineHeight: 1.4, mt: 0.25 }}>
                <LocalText>{subtitle}</LocalText>
              </Typography>
            </Box>
          ) : titleNode}

          {titleAction && (
            <Box sx={{ flexShrink: 0, flexBasis: { xs: '100%', sm: 'auto' } }}>
              {titleAction}
            </Box>
          )}
        </Box>
      )}
      {container ? (
        <Box sx={{ backgroundColor: "#5383b3", p: { xs: "12px", sm: "20px" }, borderRadius: "20px", flex: 1 }}>
          {children}
        </Box>
      ) : children}
    </Box>
  );
};

export default ContentArea;
